/**
 * Output schemas for AI endpoints. Used both as Claude structured-output formats
 * (server) and to type results (client). Every field is required; "unknown" is null.
 */
import { z } from 'zod';

const confidence = z.enum(['high', 'low']);
const isoOrEmpty = z.string().describe('YYYY-MM-DD, or empty string if unknown');

export const harvestOcrSchema = z.object({
  date: isoOrEmpty,
  perGreenhouse: z.boolean(),
  entries: z.array(
    z.object({
      crop: z.string().describe('product code from the provided list'),
      greenhouse: z.number().int().nullable(),
      weighings: z.string(),
      kg: z.number().nullable(),
      ng: z.number().nullable(),
      confidence,
      note: z.string(),
    }),
  ),
});
export type HarvestOcr = z.infer<typeof harvestOcrSchema>;

export const salesOcrSchema = z.object({
  days: z.array(
    z.object({
      date: isoOrEmpty,
      written_total: z.number().nullable(),
      clients: z.array(
        z.object({
          client: z.string(),
          note: z.string(),
          items: z.array(
            z.object({
              crop: z.string().describe('product code from the provided list, or X'),
              cropName: z.string(),
              supplier: z.string(),
              unit: z.enum(['pack', 'kg']),
              qty: z.number().nullable(),
              price: z.number().nullable(),
              confidence,
            }),
          ),
        }),
      ),
    }),
  ),
});
export type SalesOcr = z.infer<typeof salesOcrSchema>;

export const passportSchema = z.object({
  isPassport: z.boolean(),
  surname: z.string(),
  givenNames: z.string(),
  nameNative: z.string(),
  birthDate: isoOrEmpty,
  nationality: z.string(),
  passportNo: z.string(),
  passportExpiry: isoOrEmpty,
  visaExpiry: isoOrEmpty,
});
export type PassportScan = z.infer<typeof passportSchema>;

const actionItem = z.object({ title: z.string(), owner: z.string(), due: isoOrEmpty });

export const reportSummarySchema = z.object({ summary: z.string(), tasks: z.array(actionItem) });
export type ReportSummary = z.infer<typeof reportSummarySchema>;

export const meetingSummarySchema = z.object({
  decisions: z.array(z.string()),
  actions: z.array(actionItem),
});
export type MeetingSummary = z.infer<typeof meetingSummarySchema>;

export const translateSchema = z.object({ translations: z.array(z.string()) });

export const importListSchema = z.object({
  records: z.array(
    z.object({
      name: z.string(),
      contact: z.string(),
      phone: z.string(),
      email: z.string(),
      address: z.string(),
      pay_days: z.number().int().nullable(),
      note: z.string(),
    }),
  ),
});
export type ImportList = z.infer<typeof importListSchema>;

/** Request bodies */
export const aiFileSchema = z.object({
  /** base64 without data: prefix */
  data: z.string().min(1).max(14_000_000),
  type: z.enum([
    'image/jpeg',
    'image/png',
    'image/webp',
    'image/gif',
    'application/pdf',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'text/csv',
  ]),
});
export type AiFile = z.infer<typeof aiFileSchema>;

export const aiFilesRequest = z.object({ files: z.array(aiFileSchema).min(1).max(8) });
